<template>
  <div class="space-y-10 divide-y divide-gray-900/10">
    <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 md:grid-cols-3">
      <div class="px-4 sm:px-0">
        <h2 class="text-base font-semibold leading-7 text-gray-900">Account</h2>
        <p class="mt-1 text-sm leading-6 text-gray-600">Account Text Prompt</p>
      </div>

      <Form v-slot="{ handleSubmit, errors}" :validation-schema="schema" class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
        <button v-if="accountId" @click="destroy" type="button" class="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 m-3">
          <TrashIcon class="h-5 w-5" aria-hidden="true" />
        </button>
        <div class="px-4 py-6 sm:p-8">
          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Name
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.name"
                    v-bind:class="{ 'ring-red-600': errors.name }"
                    type="text"
                    name="name"
                    id="name"
                    placeholder="Name"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="name" />
              </div>
            </div>


            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="features" class="block text-sm font-medium leading-6 text-gray-900">
                Features
              </label>
              <div class="mt-2">
                <Codemirror
                    name="features"
                    id="features"
                    v-model:value="formData.features"
                    :options="cmOptions"
                    border
                    :height="300"></Codemirror>

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="features" />
              </div>
            </div>



            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="title" class="block text-sm font-medium leading-6 text-gray-900">
                High (red) severity - target weeks
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.target_add_0"
                    v-bind:class="{ 'ring-red-600': errors.target_add_0 }"
                    type="number"
                    name="target_add_0"
                    id="target_add_0"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="target_add_0" />
              </div>
            </div>

            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="title" class="block text-sm font-medium leading-6 text-gray-900">
                Medium (amber) severity - target weeks
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.target_add_1"
                    v-bind:class="{ 'ring-red-600': errors.target_add_1 }"
                    type="number"
                    name="target_add_1"
                    id="target_add_1"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="target_add_1" />
              </div>
            </div>

            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="title" class="block text-sm font-medium leading-6 text-gray-900">
                Low (green) severity - target weeks
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.target_add_2"
                    v-bind:class="{ 'ring-red-600': errors.target_add_2 }"
                    type="number"
                    name="target_add_2"
                    id="target_add_2"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="target_add_2" />
              </div>
            </div>


            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="network" class="block text-sm font-medium leading-6 text-gray-900">
                Network
              </label>
              <div class="mt-2">
                <Field
                    name="network"
                    v-model="formData.network_id"
                    v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="networkList"
                      @option:selected="updateNetwork"
                      @option:deselected="updateNetwork"
                      :clearable="false"
                      label="name" />
                </Field>

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="network" />
              </div>
            </div>

            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="website" class="block text-sm font-medium leading-6 text-gray-900">
                Website
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field
                    name="website"
                    v-model="formData.website_id"
                    v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="websiteList"
                      @option:selected="updateWebsite"
                      @option:deselected="updateWebsite"
                      :clearable="false"
                      label="domain" />
                </Field>

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="website" />
              </div>
            </div>
          </div>


          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 mt-12 mb-2">
            <div class="sm:col-span-5">
              <div class="relative">
                <div class="absolute inset-0 flex items-center" aria-hidden="true">
                  <div class="w-full border-t border-gray-300" />
                </div>
                <div class="relative flex justify-start">
                  <span class="bg-white pr-3 text-base font-semibold leading-6 text-gray-900">Additional Information</span>
                </div>
              </div>
            </div>
          </div>


          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                FCA Firm Reference Number
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.fca_firm_reference_number"
                    v-bind:class="{ 'ring-red-600': errors.fca_firm_reference_number }"
                    type="text"
                    name="fca_firm_reference_number"
                    id="fca_firm_reference_number"
                    placeholder="FCA Firm Reference Number"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="fca_firm_reference_number" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Companies House Number
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.companies_house_number"
                    v-bind:class="{ 'ring-red-600': errors.companies_house_number }"
                    type="text"
                    name="companies_house_number"
                    id="companies_house_number"
                    placeholder="Companies House Number"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="companies_house_number" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Phone Number
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.phone_number"
                    v-bind:class="{ 'ring-red-600': errors.phone_number }"
                    type="text"
                    name="phone_number"
                    id="phone_number"
                    placeholder="Phone Number"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="phone_number" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Location
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.location"
                    v-bind:class="{ 'ring-red-600': errors.location }"
                    type="text"
                    name="location"
                    id="location"
                    placeholder="Location"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="location" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Staff
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.staff"
                    v-bind:class="{ 'ring-red-600': errors.staff }"
                    type="text"
                    name="staff"
                    id="staff"
                    placeholder="Staff"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="staff" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Client base
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.client_base"
                    v-bind:class="{ 'ring-red-600': errors.client_base }"
                    type="text"
                    name="client_base"
                    id="client_base"
                    placeholder="Client base"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="client_base" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Turnover
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.turnover"
                    v-bind:class="{ 'ring-red-600': errors.turnover }"
                    type="text"
                    name="turnover"
                    id="turnover"
                    placeholder="Turnover"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="turnover" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Platform
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.platform"
                    v-bind:class="{ 'ring-red-600': errors.platform }"
                    type="text"
                    name="platform"
                    id="platform"
                    placeholder="Platform"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="platform" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Compliance Firm
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.compliance_firm"
                    v-bind:class="{ 'ring-red-600': errors.compliance_firm }"
                    type="text"
                    name="compliance_firm"
                    id="compliance_firm"
                    placeholder="Compliance Firm"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="compliance_firm" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Practice management (back-office) technology
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.practice_management_technology"
                    v-bind:class="{ 'ring-red-600': errors.practice_management_technology }"
                    type="text"
                    name="practice_management_technology"
                    id="practice_management_technology"
                    placeholder="Practice management (back-office) technology"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="practice_management_technology" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Risk technology
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.risk_technology"
                    v-bind:class="{ 'ring-red-600': errors.risk_technology }"
                    type="text"
                    name="risk_technology"
                    id="risk_technology"
                    placeholder="Risk technology"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="risk_technology" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Cashflow technology
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.cashflow_technology"
                    v-bind:class="{ 'ring-red-600': errors.cashflow_technology }"
                    type="text"
                    name="cashflow_technology"
                    id="cashflow_technology"
                    placeholder="Cashflow technology"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="cashflow_technology" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="name" class="block text-sm font-medium leading-6 text-gray-900">
                Assets Under Management
              </label>
              <div class="mt-2">
                <Field
                    v-model="formData.assets_under_management"
                    v-bind:class="{ 'ring-red-600': errors.assets_under_management }"
                    type="text"
                    name="assets_under_management"
                    id="assets_under_management"
                    placeholder="Assets under management"
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="assets_under_management" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="firm_type" class="block text-sm font-medium leading-6 text-gray-900">
                Firm type
              </label>
              <div class="mt-2">
                <Field
                    name="firm_type"
                    v-model="formData.firm_type_id"
                    v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="firmTypesList"
                      :clearable="false"
                      label="name" />
                </Field>

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="firm_type" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="network_question_set_key_test" class="block text-sm font-medium leading-6 text-gray-900">
                Network Question Set Test (has a higher priority)
              </label>
              <span class="text-sm text-gray-900">If new question set is added, this will mean the action tracker will return to no data</span>
              <div class="mt-2">
                <Field
                    name="network_question_set_key_test"
                    v-model="formData.network_question_set_key_test"
                    v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.key"
                      :options="questionSetsList"
                      :clearable="true"
                      label="name" />
                </Field>
                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="network_question_set_key_test" />
              </div>
            </div>

            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="product" class="block text-sm font-medium leading-6 text-gray-900">
                Product
              </label>
              <div class="mt-2">
                <Field
                    name="product"
                    v-model="formData.product_id"
                    v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="productsList"
                      :clearable="false"
                      label="name" />
                </Field>

                <ErrorMessage
                    class="mt-2 text-xs text-red-600"
                    name="product" />
              </div>
            </div>

          </div>


        </div>
        <div class="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
          <button @click="cancel" type="button" class="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
          <button v-if="accountId" @click="handleSubmit($event, update)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Update</button>
          <button v-else @click="handleSubmit($event, create)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Save</button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import { TrashIcon } from '@heroicons/vue/24/solid'
import TrixComponent from "../TrixComponent.vue";

export default {
  name: "AccountCreateEditFormComponent",

  props: {
    accountId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      websiteList: [],
      networkList: [{id: null, name: '---'}],
      firmTypesList: [{id: null, name: '---'}],
      questionSetsList: [{key: null, name: '---'}],
      productsList: [],
      formData: {
        name: null,
        features: null,
        target_add_0: 4,
        target_add_1: 8,
        target_add_2: 13,
        website_id: 1,
        network_id: null,
        fca_firm_reference_number: null,
        companies_house_number: null,
        phone_number: null,
        location: null,
        staff: null,
        client_base: null,
        turnover: null,
        platform: null,
        compliance_firm: null,
        practice_management_technology: null,
        risk_technology: null,
        cashflow_technology: null,
        assets_under_management: null,
        firm_type_id: null,
        product_id: null,
        network_question_set_key_test: null,
      },
      cmOptions: {
        mode: "application/json",
        // lineNumbers: true,
        // line: true,
        // lineWrapping: true,
        // foldGutter: true,
        // gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
      },
    }
  },
  components: {
    TrixComponent,
    TrashIcon,
  },
  computed: {
    schema() {
      let validator = yup.object({
        name: yup.string().required().min(2).label('Name'),
        fca_firm_reference_number: yup.string().nullable().min(2).label('FCA Firm Reference Number'),
        companies_house_number: yup.string().nullable().min(2).label('Companies House Number'),
        phone_number: yup.string().nullable().min(2).label('Phone Number'),
        location: yup.string().nullable().min(2).label('Location'),
        staff: yup.string().nullable().min(2).label('Staff'),
        client_base: yup.string().nullable().min(2).label('Client base'),
        turnover: yup.string().nullable().min(2).label('Turnover'),
        platform: yup.string().nullable().min(2).label('Platform'),
        compliance_firm: yup.string().nullable().min(2).label('Compliance Firm'),
        practice_management_technology: yup.string().nullable().min(2).label('Practice management (back-office) technology'),
        risk_technology: yup.string().nullable().min(2).label('Risk technology'),
        cashflow_technology: yup.string().nullable().min(2).label('Cashflow technology'),
        assets_under_management: yup.string().nullable().min(2).label('Assets under management'),
        firm_type: yup.mixed().nullable().label('Firm type'),
        network_question_set_key_test: yup.mixed().nullable().label('Network Question Set'),
      });

      if (this.isAdmin) {
        validator = validator.shape({
          website: yup.number().required().label('Website'),
          network: yup.mixed().nullable().label('Network'),
          product: yup.mixed().nullable().label('Product'),
        });
      }

      return validator;
    },
    isAdmin() {
      return this.$store.state.userIsAccountLevelAdmin;
    },
  },
  methods: {
    getProductsList() {
      axios.get(`/member-firm/account/form-data/products-list`).then((res) => {
        this.productsList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getDefaultFeaturesList() {
      axios.get(`/member-firm/account/form-data/default-features-list`).then((res) => {
        this.formData.features = res.data.default;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getFirmTypesList() {
      axios.get(`/member-firm/question-section/form-data/firm-types-list`).then((res) => {
        this.firmTypesList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getQuestionSetsList() {
      axios.get(`/member-firm/question-section/form-data/question-sets-list`).then((res) => {
        this.questionSetsList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getWebsiteList() {
      axios.get(`/member-firm/question-section/form-data/website-list`).then((res) => {
        this.websiteList = res.data.data;
        // this.getRegulatoryKeyList();
      }).catch((error) => {
        console.log('error', error)
      })
    },
    updateWebsite() {
      this.formData.network_id = null;
      this.formData.parent_id = null;
      this.getNetworkList();
    },
    getNetworkList() {
      axios.get(`/member-firm/question-section/form-data/network-list/${this.formData.website_id}`).then((res) => {
        this.networkList = [{id: null, name: '---'}, ...res.data.data];
        // this.getRegulatoryKeyList();
      }).catch((error) => {
        console.log('error', error)
      })
    },
    updateNetwork() {
      this.formData.parent_id = null;
      // this.getRegulatoryKeyList();
    },
    getAccount() {
      axios.get(`/member-firm/accounts/edit/${this.accountId}`).then((res) => {
        this.formData = res.data.data;
        // for admin
        if (this.isAdmin) {
          this.getWebsiteList();
          this.getNetworkList();
          this.getProductsList();
        }
      }).catch((error) => {
        console.log('error', error)
      })
    },
    update() {
      axios.put(`/member-firm/accounts/update/${this.accountId}`, this.formData)
          .then((res) => {
            this.$emit('reloadIndex');
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
            this.$emit('cancel');
          })
          .catch((error) => {
            console.log('error', error);
            this.$emit('reloadIndex');
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    create() {
      axios.post(`/member-firm/accounts/store`, this.formData)
          .then((res) => {
            this.$emit('reloadIndex');
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
            this.$emit('cancel');
          })
          .catch((error) => {
            console.log('error', error);
            this.$emit('reloadIndex');
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    cancel() {
      this.$emit('cancel');
    },
    destroy() {
      if(confirm("Do you really want to delete?")) {
        axios.delete(`/member-firm/accounts/destroy/${this.accountId}`).then((res) => {
          this.$emit('cancel');
          this.$emit('reloadIndex');
          this.$notify({ type: "success", title: "Successfully", text: res.data.message });
        }).catch((error) => {
          console.log('error', error)
          this.$notify({ title: "Unsuccessfully", text: error.message });
          this.$emit('reloadIndex');
        })
      }
    },
  },
  created() {
    if(this.accountId){
      this.getAccount();
    } else {
      this.getDefaultFeaturesList();
      // for admin
      if (this.isAdmin) {
        this.getWebsiteList();
        this.getNetworkList();
        this.getProductsList();
      }
    }
    this.getFirmTypesList();
    this.getQuestionSetsList();
  }
}
</script>

<style scoped>
  @import 'vue-select/dist/vue-select.css';
</style>